import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Flame, Timer, Trophy, TrendingUp, Dumbbell, Activity } from "lucide-react";
import { useReveal } from "@/lib/useReveal";
import { CountUp } from "./CountUp";

const weeklyData = [
  { day: "Mon", minutes: 42, volume: 4200 },
  { day: "Tue", minutes: 55, volume: 5600 },
  { day: "Wed", minutes: 30, volume: 2600 },
  { day: "Thu", minutes: 48, volume: 4800 },
  { day: "Fri", minutes: 62, volume: 6200 },
  { day: "Sat", minutes: 75, volume: 7400 },
  { day: "Sun", minutes: 20, volume: 1800 },
];

const zonesData = [
  { name: "Z1", value: 18, color: "#2EE59D" },
  { name: "Z2", value: 42, color: "#0FF0FC" },
  { name: "Z3", value: 24, color: "#BFFF00" },
  { name: "Z4", value: 12, color: "#FF6B00" },
  { name: "Z5", value: 4, color: "#FF4D4D" },
];

const sessions = [
  { name: "Push Day · PR Builder", date: "Today", minutes: 55, rpe: 8.2, kcal: 482 },
  { name: "Zone 2 Endurance", date: "Yesterday", minutes: 40, rpe: 5.6, kcal: 310 },
  { name: "Neon Burn HIIT", date: "Mon", minutes: 28, rpe: 9.1, kcal: 420 },
];

const tooltipStyle = {
  background: "rgba(15,22,32,0.95)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 12,
  color: "#F6F7FB",
  fontSize: 12,
  padding: "8px 10px",
};

export const ProgressDemo = () => {
  const [tab, setTab] = useState("overview");
  const headerRef = useReveal();
  const cardRef = useReveal({ threshold: 0.1 });

  return (
    <section id="progress" data-testid="progress-section" className="relative py-20 sm:py-24">
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(600px circle at 10% 30%, rgba(15,240,252,0.08), transparent 60%), radial-gradient(600px circle at 90% 80%, rgba(191,255,0,0.06), transparent 60%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Left copy */}
          <div ref={headerRef} className="reveal lg:col-span-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/70">
              <TrendingUp className="h-3.5 w-3.5 text-neon-cyan" />
              Progress Dashboard
            </div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-white leading-[0.95]">
              SEE THE WORK.
              <br />
              <span className="text-neon-cyan">TRUST THE TRAJECTORY.</span>
            </h2>
            <p className="mt-4 text-white/70">
              Every rep, minute, and heartbeat flows into one clean view — so you know exactly what&rsquo;s working.
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <KpiChip icon={Timer} label="Weekly minutes" value={<CountUp end={332} suffix="m" />} accent="lime" testid="progress-kpi-minutes" />
              <KpiChip icon={Flame} label="Calories" value={<CountUp end={2842} suffix=" kcal" />} accent="orange" testid="progress-kpi-calories" />
              <KpiChip icon={Trophy} label="Active streak" value={<><CountUp end={27} /> days</>} accent="cyan" testid="progress-kpi-streak" />
              <KpiChip icon={Dumbbell} label="New PRs" value={<><CountUp end={4} /> this mo.</>} accent="lime" testid="progress-kpi-prs" />
            </div>

            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-xs uppercase tracking-widest text-white/55 mb-2">Consistency</div>
              <StreakGrid />
            </div>
          </div>

          {/* Dashboard mock */}
          <div ref={cardRef} className="reveal lg:col-span-8">
            <Card
              data-testid="progress-dashboard-card"
              className="bg-white/[0.03] border-white/10 rounded-2xl overflow-hidden shadow-soft"
            >
              <CardContent className="p-0">
                <div className="flex items-center justify-between border-b border-white/10 p-4">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-neon-lime pulse-dot" />
                    <span className="text-sm text-white/80">Alex &middot; Week 7 &middot; Intermediate</span>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-white/50">Last sync &middot; 2 min ago</div>
                </div>

                <Tabs value={tab} onValueChange={setTab} className="p-4">
                  <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl h-auto">
                    <TabsTrigger value="overview" data-testid="progress-tab-overview" className="px-3 py-1.5 text-xs data-[state=active]:bg-neon-lime data-[state=active]:text-ink-0">Overview</TabsTrigger>
                    <TabsTrigger value="workouts" data-testid="progress-tab-workouts" className="px-3 py-1.5 text-xs data-[state=active]:bg-neon-lime data-[state=active]:text-ink-0">Workouts</TabsTrigger>
                    <TabsTrigger value="zones" data-testid="progress-tab-zones" className="px-3 py-1.5 text-xs data-[state=active]:bg-neon-lime data-[state=active]:text-ink-0">HR Zones</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="mt-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <StatBox label="Active minutes" value={<CountUp end={332} />} trend="+18%" />
                      <StatBox label="Tonnage" value={<><CountUp end={32.6} decimals={1} /> T</>} trend="+7%" />
                      <StatBox label="Avg RPE" value={<CountUp end={7.4} decimals={1} />} trend="stable" trendColor="cyan" />
                    </div>
                    <div data-testid="progress-chart-weekly" className="mt-4 rounded-xl border border-white/10 bg-ink-1/60 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm text-white/75 font-medium">Weekly active minutes</div>
                        <div className="text-xs text-white/50">Last 7 days</div>
                      </div>
                      <div className="h-52">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={weeklyData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                            <CartesianGrid stroke="rgba(255,255,255,0.07)" vertical={false} />
                            <XAxis dataKey="day" stroke="rgba(255,255,255,0.45)" tickLine={false} axisLine={false} fontSize={11} />
                            <YAxis stroke="rgba(255,255,255,0.45)" tickLine={false} axisLine={false} fontSize={11} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "rgba(191,255,0,0.35)" }} />
                            <Line type="monotone" dataKey="minutes" stroke="#BFFF00" strokeWidth={2.5} dot={{ r: 3, fill: "#BFFF00" }} activeDot={{ r: 5 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="workouts" className="mt-4">
                    <div data-testid="progress-chart-volume" className="rounded-xl border border-white/10 bg-ink-1/60 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm text-white/75 font-medium">Volume (kg) this week</div>
                        <div className="text-xs text-white/50">Strength sessions</div>
                      </div>
                      <div className="h-52">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={weeklyData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                            <CartesianGrid stroke="rgba(255,255,255,0.07)" vertical={false} />
                            <XAxis dataKey="day" stroke="rgba(255,255,255,0.45)" tickLine={false} axisLine={false} fontSize={11} />
                            <YAxis stroke="rgba(255,255,255,0.45)" tickLine={false} axisLine={false} fontSize={11} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(15,240,252,0.08)" }} />
                            <Bar dataKey="volume" fill="#0FF0FC" radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="zones" className="mt-4">
                    <div className="grid gap-4 md:grid-cols-2 items-center">
                      <div data-testid="progress-chart-zones" className="rounded-xl border border-white/10 bg-ink-1/60 p-4">
                        <div className="text-sm text-white/75 font-medium mb-2">Heart-rate zone mix</div>
                        <div className="h-56">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Tooltip contentStyle={tooltipStyle} />
                              <Pie data={zonesData} dataKey="value" innerRadius={48} outerRadius={80} paddingAngle={3} stroke="none">
                                {zonesData.map((z, i) => (
                                  <Cell key={i} fill={z.color} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <ul className="space-y-2">
                        {zonesData.map((z) => (
                          <li key={z.name} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ background: z.color }} />
                              <span className="text-sm text-white/80">Zone {z.name}</span>
                            </div>
                            <span className="font-mono-num text-sm text-white">{z.value}%</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Recent sessions */}
                <div className="border-t border-white/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-white/75 font-medium">Recent sessions</div>
                    <div className="text-xs text-white/50">Auto-logged</div>
                  </div>
                  <div className="divide-y divide-white/5 rounded-xl border border-white/10 overflow-hidden">
                    {sessions.map((s, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3 bg-white/[0.02]">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-neon-lime/10 border border-neon-lime/25 text-neon-lime">
                            <Activity className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm text-white truncate">{s.name}</div>
                            <div className="text-xs text-white/50">{s.date}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-white/75 font-mono-num">
                          <span>{s.minutes}m</span>
                          <span className="hidden sm:inline">RPE {s.rpe}</span>
                          <span className="text-neon-orange">{s.kcal}kcal</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

const KpiChip = ({ icon: Icon, label, value, accent = "lime", testid }) => {
  const palette =
    accent === "lime"
      ? "text-neon-lime bg-neon-lime/10 border-neon-lime/25"
      : accent === "cyan"
      ? "text-neon-cyan bg-neon-cyan/10 border-neon-cyan/25"
      : "text-neon-orange bg-neon-orange/10 border-neon-orange/25";
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-center gap-3"
    >
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border ${palette}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-white/55 truncate">{label}</div>
        <div className="text-sm text-white font-semibold font-mono-num truncate">{value}</div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value, trend, trendColor = "lime" }) => {
  const trendClass =
    trendColor === "lime"
      ? "text-neon-lime bg-neon-lime/10 border-neon-lime/25"
      : "text-neon-cyan bg-neon-cyan/10 border-neon-cyan/25";
  return (
    <div className="rounded-xl border border-white/10 bg-ink-1/60 p-4">
      <div className="text-[10px] uppercase tracking-widest text-white/55">{label}</div>
      <div className="mt-1 flex items-end justify-between gap-3">
        <div className="font-display text-3xl text-white leading-none">{value}</div>
        <span className={`text-[11px] rounded-full border px-2 py-0.5 ${trendClass}`}>{trend}</span>
      </div>
    </div>
  );
};

const StreakGrid = () => {
  // 7 columns x 5 rows = 35 cells (~last 5 weeks)
  const cells = Array.from({ length: 35 }, (_, i) => {
    const r = (i * 9301 + 49297) % 233280;
    const n = r / 233280;
    return n > 0.72 ? 2 : n > 0.38 ? 1 : 0;
  });
  const colorFor = (v) =>
    v === 2 ? "bg-neon-lime shadow-[0_0_8px_rgba(191,255,0,0.5)]" : v === 1 ? "bg-neon-lime/35" : "bg-white/6";
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {cells.map((v, i) => (
        <div key={i} className={`h-4 rounded-sm ${colorFor(v)}`} />
      ))}
    </div>
  );
};
